export const BIORHYTHM_PERIODS = { physical: 23, emotional: 28, intellectual: 33 } as const

export interface BiorhythmValues {
  physical: number
  emotional: number
  intellectual: number
}

const DAY_MS = 86_400_000

/** Parse a `YYYY-MM-DD` quiz answer into a local calendar date. */
export function parseBirthDate(dob: string | undefined): Date | null {
  if (!dob) return null
  const [y, m, d] = dob.split("-").map(Number)
  if (!y || !m || !d) return null

  const birthDate = new Date(y, m - 1, d)
  if (Number.isNaN(birthDate.getTime())) return null

  return birthDate
}

const localMidnight = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

export function daysOfLife(birth: Date, target: Date): number {
  return Math.round((localMidnight(target) - localMidnight(birth)) / DAY_MS)
}

/** Biorhythm value (-100…100) for a given day of life + cycle length. */
export function biorhythmPoint(dayOfLife: number, period: number): number {
  return Math.round(100 * Math.sin((2 * Math.PI * dayOfLife) / period))
}

export function todayBiorhythm(birth: Date, today = new Date()): BiorhythmValues {
  const days = daysOfLife(birth, today)
  return {
    physical: biorhythmPoint(days, BIORHYTHM_PERIODS.physical),
    emotional: biorhythmPoint(days, BIORHYTHM_PERIODS.emotional),
    intellectual: biorhythmPoint(days, BIORHYTHM_PERIODS.intellectual),
  }
}

export interface BiorhythmSeries {
  /** dd.MM labels for the left / centre(today) / right edges. */
  labels: { left: string; center: string; right: string }
  physical: number[]
  emotional: number[]
  intellectual: number[]
  /** Index of today within each series. */
  todayIndex: number
}

const ddmm = (date: Date) =>
  `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}`

/** Build the ±`span`-day biorhythm curves centred on today. */
export function buildSeries(birth: Date, span = 25, today = new Date()): BiorhythmSeries {
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const baseDays = daysOfLife(birth, todayLocal)
  const offsets = Array.from({ length: span * 2 + 1 }, (_, i) => i - span)
  const series = (period: number) => offsets.map((o) => biorhythmPoint(baseDays + o, period))
  const dateAt = (offset: number) =>
    new Date(todayLocal.getFullYear(), todayLocal.getMonth(), todayLocal.getDate() + offset)

  return {
    labels: {
      left: ddmm(dateAt(-span)),
      center: ddmm(dateAt(0)),
      right: ddmm(dateAt(span)),
    },
    physical: series(BIORHYTHM_PERIODS.physical),
    emotional: series(BIORHYTHM_PERIODS.emotional),
    intellectual: series(BIORHYTHM_PERIODS.intellectual),
    todayIndex: span,
  }
}
